import { useRouter } from 'next/router';
import { RPCSchemas } from '@bits/rpc';
import { AdvancedFilters } from '@bits/ui';
import { EventLog, EventLogEvent } from '@bits/ui/event-log/event-log';
import {
  BuildingOffice,
  Cube,
  Eye,
  File,
  Gauge,
  Minus,
  Plugs,
  User,
  UsersThree,
} from '@phosphor-icons/react';

import { useFilteredAuditTrail } from '@/components/audit-trail/use-filtered-audit-trail';
import { UserDisplayer } from '@/components/user-picker/user-displayer';

export const AuditTrail = ({ resourceId }: { resourceId?: string }) => {
  const { filteredList, request, results, pageSize } =
    useFilteredAuditTrail(resourceId);
  const router = useRouter();

  const isLoading =
    (request.isLoading || !filteredList.ready) &&
    results.auditLogs.length === 0;
  const isNavigating = request.isLoading && results.auditLogs.length !== 0;

  return (
    <div className="flex flex-col gap-4">
      <AdvancedFilters
        filters={filteredList.filterOptions ?? []}
        activeFilters={filteredList.activeFilters}
        onFiltersChange={filteredList.setFilters}
        activeSavedFilter={filteredList.activeSavedFilter}
        onActiveSavedFilterChange={filteredList.setActiveSavedFilter}
        savedFilters={filteredList.savedFilters}
        onSavedFiltersChange={filteredList.setSavedFilters}
      />

      <EventLog
        accessibleName="Audit Trail"
        loading={isLoading}
        navigating={isNavigating}
        events={results.auditLogs.map(transformAuditLogEventLogEvent)}
        pagination={{
          page: filteredList.page,
          totalPages: Math.ceil(results.total / pageSize),
          onPageChange: (page) => {
            router.push({
              query: {
                ...router.query,
                page,
              },
            });
          },
        }}
      />
    </div>
  );
};

const transformAuditLogEventLogEvent = (
  log: RPCSchemas['AuditLog']
): EventLogEvent<string> => {
  const property = extractProperty(log.event.eventType);

  return {
    id: log.eventId,
    createdAt: new Date(log.publishedAt),
    actor: {
      name: getActor(log),
    },
    label: (
      <>
        {getNoun(log.event.eventType)}{' '}
        <span className="inline-block rounded-full border p-1 px-2">
          {property}
        </span>
      </>
    ),
    icon: getPropertyIcon(property),
    type: log.event.eventType,
    rawPayload: log.event,
  };
};

const getActor = (log: RPCSchemas['AuditLog']) => {
  if ('source' in log.event) {
    if ('customerUserId' in log.event.source) {
      return (
        <UserDisplayer
          // Hackilyhack to align this inline
          className="relative top-2 h-0"
          userId={log.event.source.customerUserId}
        />
      );
    }

    return (
      actorMap[log.event.source.type as keyof typeof actorMap] ??
      actorMap._DEFAULT
    );
  }

  if ('actor' in log.event && 'customerUserId' in log.event.actor) {
    return (
      <UserDisplayer
        // Hackilyhack to align this inline
        className="relative top-2 h-0"
        userId={log.event.actor.customerUserId as string}
      />
    );
  }

  if ('customerUserId' in log.event) {
    return (
      <UserDisplayer
        className="relative top-2 h-0"
        userId={log.event.customerUserId as string}
      />
    );
  }

  return actorMap._DEFAULT;
};

const actorMap = {
  APPLICATION_PROVIDER: 'Application',
  CUSTOMER_USER: 'User',
  _DEFAULT: 'System',
};

const getPropertyIcon = (property: string) => {
  const key =
    propertyIconKeys.find((key) => property.toLowerCase().includes(key)) ||
    '_DEFAULT';

  return propertyIconMap[key as keyof typeof propertyIconMap];
};

const propertyIconMap = {
  risk: Gauge,
  document: File,
  property: Cube,
  organization: BuildingOffice,
  relation: Plugs,
  combination: UsersThree,
  individual: User,
  monitor: Eye,
  _DEFAULT: Minus,
};

const propertyIconKeys = Object.keys(propertyIconMap);

const getNoun = (str: string) => {
  return str.match(/([A-Z]([a-z]+))Event(Type)?$/)?.[1]?.toLowerCase() ?? '';
};

const extractProperty = (str: string) => {
  // Extract the property
  const matchedString =
    str.match(/^(.+)[A-Z]([a-z]+)Event(Type)?$/)?.[1] ?? str;
  // Split on capital letters, but not if there are more than one in a row
  const splitOnCapitals = matchedString.split(/(?=[A-Z][a-z])/);
  // Capitalize the first letter only
  return splitOnCapitals
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
};
